import type { FareClassWithReward } from 'constants/flight'
import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import { ALL_CHARTER_FARE_CLASSES } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { expandMaldivesCharterFareClassesWithRewards } from 'lib/maldivesCharter/charterCabins'

/**
 * Full charter fare order for calendar "All classes" mode: every available cabin from economy
 * through business (cash then matching reward when present), without upsell slicing from the URL cabin.
 */
export function orderMaldivesCharterFareClassesFullPresentation(
  availableFareClasses: ReadonlyArray<FareClassWithReward>,
): Array<FareClassWithReward> {
  const available = new Set<FareClassWithReward>(availableFareClasses)
  return expandMaldivesCharterFareClassesWithRewards([...ALL_CHARTER_FARE_CLASSES]).filter(fc => available.has(fc))
}

function dedupeFareClassesInOrder(fareClasses: Array<FareClassWithReward>): Array<FareClassWithReward> {
  return fareClasses.filter((fc, i, arr) => arr.indexOf(fc) === i)
}

/** Matches {@link orderFareCarouselClassesBySelectedFirst} without a components-layer dependency. */
function moveSelectedFareClassFirst(
  fareClasses: Array<FareClassWithReward>,
  selectedFareClass: FareClassWithReward,
): Array<FareClassWithReward> {
  const unique = dedupeFareClassesInOrder(fareClasses)
  if (!unique.includes(selectedFareClass)) {
    return unique
  }
  return [selectedFareClass, ...unique.filter(fc => fc !== selectedFareClass)]
}

/**
 * Layout B (desktop trip-options table): when the guest filters the calendar by a Maldives charter
 * cabin, show columns for that cabin and upsells only — same cabin first (cash then Classic
 * Rewards when both exist in {@link availableFareClasses}), then higher cabins in charter order.
 *
 * Non-Maldives-charter fare classes fall back to "selected class first" carousel ordering.
 */
export function orderMaldivesCharterTableFareClassesForSelectedFilter(
  availableFareClasses: Array<FareClassWithReward>,
  selectedFareClass: FareClassWithReward,
): Array<FareClassWithReward> {
  const baseSelected = toBaseFareClass(selectedFareClass)
  const startIdx = ALL_CHARTER_FARE_CLASSES.indexOf(baseSelected)
  if (startIdx === -1) {
    return moveSelectedFareClassFirst(availableFareClasses, selectedFareClass)
  }

  // Show only the selected cabin's cash + reward columns (no upsells)
  const selectedCabinOnly = [baseSelected]
  const expanded = expandMaldivesCharterFareClassesWithRewards(selectedCabinOnly)
  const availableSet = new Set<FareClassWithReward>(availableFareClasses)
  return expanded.filter(fc => availableSet.has(fc))
}
